// Docs: FEATURES.md "Daily study loop"; IMPLEMENTATION.md §3.3.
// One source of truth for "what does ▶ do right now?". Feeds the Coach
// shell's center button, the Home hero card and the Studio shell's
// "weiter üben" button so they can never disagree.
//
// Priority (mirrors the daily loop):
//   1. a started daily lesson  → continue it
//   2. the next planned lesson → start it
//   3. no plan yet             → plan the day
//   4. everything done, but a lesson of your own (Erstellen) is open
//                              → continue / start it (`bonus`: ▶ stays green)
//   5. everything done, none of your own, a Nebenspur has cards
//                              → resume / start its session (`bonus` too)
//   6. everything done         → bonus: the Erstellen window on Heute
// While the inbox is loading or can't be read (no API key, offline) the mode
// is 'unavailable' — never 'done': an unreadable plan is not a finished one;
// ▶ then just goes to Heute, which explains what is missing.
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { inboxAPI, sidetrackAPI } from '../services/api';
import { queryKeys } from '../queryKeys';
import { creatorPath, openLesson } from '../utils/lessonRoutes';
import {
  estimateRemainingMinutes,
  lessonShapeFromItem,
  remainingQuestions,
} from '../utils/inboxLesson';

// The Inbox's plan-day mutation carries this key so the hero card can show
// "Plan wird erstellt" (useIsMutating) without owning the mutation.
export const PLAN_DAY_MUTATION_KEY = ['plan-day'];

const hasAnswers = (item) => (item.payload?.answered || 0) > 0;
const isReady = (item) => item.payload?.status !== 'being_initialized';
// Not yet answered through (a finished lesson can linger as 'active').
const isOpen = (item) => {
  const total = item.payload?.total_questions || 0;
  return total === 0 || (item.payload?.answered || 0) < total;
};
// Nothing left in today's plan: no lesson, no plan to make, no setup pending.
const isPlanDone = (data) =>
  !(data?.today || []).some((i) => ['lesson', 'plan_day', 'onboarding'].includes(i.kind));
// A Nebenspur row's open session, if it still has cards to go.
const sidetrackOpenLesson = (item) => {
  const open = item.payload?.open_lesson;
  return open && (open.answered || 0) < (open.total_questions || 0) ? open : null;
};
const sidetrackActionable = (item) =>
  !!sidetrackOpenLesson(item) || (item.payload?.due || 0) > 0 || (item.payload?.new || 0) > 0;
const bonusWaiting = (data) => {
  const open = (data?.custom || []).filter(isOpen);
  return isPlanDone(data) && open.length > 0 && !open.some(isReady);
};

export function useNextLesson() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.inbox.all,
    queryFn: inboxAPI.get,
    staleTime: 30_000,
    // Keep ▶ / NextUp in sync with practice progress on app refocus.
    refetchOnWindowFocus: true,
    // A bonus lesson still being written turns ready on the server only.
    refetchInterval: (query) => (bonusWaiting(query.state.data) ? 4000 : false),
  });

  const today = data?.today || [];
  const lessonItems = today.filter((i) => i.kind === 'lesson');
  const planDay = today.find((i) => i.kind === 'plan_day');
  // While setup is pending the backend suppresses plan_day, so "no lessons
  // and no plan_day" does not mean the day is done — it means "set up first".
  const onboarding = today.find((i) => i.kind === 'onboarding');

  // "Started" means the user has answered something. `started_at` is NOT a
  // progress signal — the backend sets it when the lesson is created, so
  // every inbox payload carries it.
  // Right after planning, lessons arrive while their questions are still
  // being written; offer the first one that is ready rather than the first
  // in the list.
  // Once the plan is done, a lesson you made yourself (inbox `custom`, newest
  // first) is next: the newest ready one, so an extra lesson made after the
  // day lands on the hero card and ▶ right away.
  const planDone = !!data && isPlanDone(data);
  const ownBonus = planDone && (data.custom || []).some(isOpen);
  const candidates = ownBonus ? data.custom.filter(isOpen) : lessonItems;
  const started = ownBonus ? null : candidates.find(hasAnswers);
  const ready = candidates.find(isReady);
  let next = started || ready || candidates[0] || null;

  // With none of your own open, a Nebenspur with cards is next: one whose
  // session is still open first, else the first with due or new cards.
  const sidetracks = planDone && !ownBonus ? today.filter((i) => i.kind === 'sidetrack') : [];
  const sidetrackItem = sidetracks.find(sidetrackOpenLesson) || sidetracks.find(sidetrackActionable) || null;
  if (sidetrackItem && sidetrackOpenLesson(sidetrackItem)) {
    next = { payload: sidetrackOpenLesson(sidetrackItem) };
  }
  const sidetrack = sidetrackItem
    ? {
        label: sidetrackItem.payload.label,
        due: sidetrackItem.payload.due || 0,
        new: sidetrackItem.payload.new || 0,
      }
    : null;
  const bonus = ownBonus || !!sidetrack;
  const lesson = next ? lessonShapeFromItem(next) : null;

  // A Nebenspur without an open session builds one on the server first.
  const startSidetrack = useMutation({
    mutationFn: (label) => sidetrackAPI.start(label),
    onSuccess: (payload) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.lessons.active() });
      openLesson(navigate, lessonShapeFromItem({ payload }));
    },
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.inbox.all }),
  });

  let mode; // 'continue' | 'start' | 'plan' | 'setup' | 'done' | 'unavailable'
  if (!data) mode = 'unavailable';
  else if (lesson && hasAnswers(next)) mode = 'continue';
  else if (lesson || sidetrack) mode = 'start';
  else if (planDay) mode = 'plan';
  else if (onboarding) mode = 'setup';
  else mode = 'done';

  const remaining = remainingQuestions(lesson);

  // Position within today's plan ("Plan 2/4") and a duration estimate from
  // the user's own pace for this lesson type (see estimateRemainingMinutes).
  const planTotal = lessonItems.length;
  const planPosition = next && !bonus ? lessonItems.indexOf(next) + 1 : 0;
  const estimatedMinutes = estimateRemainingMinutes(lesson);

  const label = {
    continue: 'weiter',
    start: 'starten',
    plan: 'planen',
    setup: 'einrichten',
    done: 'bonus',
    unavailable: 'üben',
  }[mode];

  // One tap from anywhere: open the runner / the planner / the creator.
  const act = useCallback(() => {
    if ((mode === 'continue' || mode === 'start') && lesson) {
      openLesson(navigate, lesson);
    } else if (mode === 'start' && sidetrack) {
      if (!startSidetrack.isPending) startSidetrack.mutate(sidetrack.label);
    } else if (mode === 'setup') {
      navigate('/onboarding');
    } else if (mode === 'plan') {
      // Home expands the Plan-Day row when asked to.
      navigate('/', { state: { expandPlanDay: true } });
    } else if (mode === 'done') {
      navigate(creatorPath());
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, lesson?.id, sidetrack?.label, startSidetrack.isPending, navigate]);

  return {
    isLoading,
    mode,
    label,
    lesson,
    // Extra practice after the plan was done: a lesson of your own, or a
    // Nebenspur (`sidetrack`: { label, due, new }; `lesson` is its open
    // session, if any).
    bonus,
    sidetrack,
    startingSidetrack: startSidetrack.isPending,
    sidetrackFailed: startSidetrack.isError,
    remaining,
    planPosition,
    planTotal,
    estimatedMinutes,
    openLessonCount: lessonItems.length,
    // Every lesson of today's plan is still being generated.
    generating: !!lesson && lesson.status === 'being_initialized',
    act,
  };
}
